import { supabase } from './supabase';
import { noderr, DatabaseError } from '../../shared/utils/errorHandler';
import { fetchMetaInsightMetrics, normalizeMetricsRow } from './metaMetrics';
import type { Campaign, CampaignsFilter } from '../../shared/types/entities';

export class CampaignService {
  private mapMetaCampaignToCampaign(metaCampaign: any, releaseOverride?: any): Campaign {
    const normalizedStatus = (metaCampaign.status || metaCampaign.normalized_status || '').toLowerCase();
    const status: Campaign['status'] =
      normalizedStatus === 'active'
        ? 'active'
        : normalizedStatus === 'paused'
          ? 'paused'
          : normalizedStatus === 'archived'
            ? 'completed'
            : 'draft';

    const release = releaseOverride ?? metaCampaign.release ?? undefined;
    const releaseId = releaseOverride?.id || metaCampaign.internal_campaign_id || metaCampaign.release_id || null;

    return {
      id: metaCampaign.id,
      name: metaCampaign.name,
      status,
      start_date: metaCampaign.start_date || metaCampaign.start_time,
      end_date: metaCampaign.end_date || metaCampaign.stop_time,
      budget:
        metaCampaign.computed_budget ||
        metaCampaign.budget ||
        metaCampaign.daily_budget ||
        metaCampaign.lifetime_budget ||
        0,
      created_at: metaCampaign.created_at,
      updated_at: metaCampaign.updated_at,
      release_id: releaseId || undefined,
      client_id: metaCampaign.client_id,
      source: 'meta',
      meta_campaign_id: metaCampaign.campaign_id,
      release,
    } as Campaign;
  }

  private notifyCampaignsChanged() {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new Event('campaigns-updated'));
    }
  }

  private async fetchAssociationRows(
    entityType: 'release' | 'artist',
    entityIds: string[]
  ): Promise<Array<{ campaign_id: string; entity_id: string }>> {
    if (!entityIds.length) return [];

    const rows: Array<{ campaign_id: string; entity_id: string }> = [];
    const chunkSize = 100;

    for (let i = 0; i < entityIds.length; i += chunkSize) {
      const chunk = entityIds.slice(i, i + chunkSize);
      const [error, result] = await noderr(
        supabase
          .from('campaign_associations')
          .select('campaign_id, entity_id')
          .eq('campaign_type', 'meta')
          .eq('entity_type', entityType)
          .in('entity_id', chunk)
      );

      if (error) {
        console.error('Error fetching campaign associations for', entityType, error);
        throw new DatabaseError(`Failed to fetch campaign associations: ${error.message}`);
      }

      rows.push(...(((result as any)?.data as Array<{ campaign_id: string; entity_id: string }>) ?? []));
    }

    return rows;
  }

  private async fetchMetaCampaignsByIds(ids: string[]): Promise<any[]> {
    if (!ids.length) return [];

    const records: any[] = [];
    const chunkSize = 50;

    for (let i = 0; i < ids.length; i += chunkSize) {
      const chunk = ids.slice(i, i + chunkSize);
      const [error, result] = await noderr(
        supabase
          .from('meta_campaigns')
          .select('*, release:releases!internal_campaign_id(*, artist:artists(*))')
          .in('id', chunk)
      );

      if (error) {
        console.error('Error fetching Meta campaigns by IDs:', error);
        throw new DatabaseError(`Failed to fetch campaigns: ${error.message}`);
      }

      records.push(...(((result as any)?.data as any[]) ?? []));
    }

    return records;
  }

  // Get campaigns linked to a specific artist
  async getCampaignsByArtist(artistId: string): Promise<Campaign[]> {
    console.debug('CampaignService.getCampaignsByArtist called with artistId:', artistId);

    const campaignIds = new Set<string>();

    // Campaigns directly linked via unified view
    const [viewError, viewResult] = await noderr(
      supabase
        .from('unified_campaigns')
        .select('id, release_id')
        .eq('artist_id', artistId)
    );

    if (viewError) {
      console.error('Error fetching campaigns by artist from view:', viewError);
      throw new DatabaseError(`Failed to fetch campaigns for artist: ${viewError.message}`);
    }

    const directRows = (((viewResult as any)?.data) ?? []) as Array<{ id: string; release_id?: string | null }>;
    directRows.forEach((row) => {
      if (row.id) campaignIds.add(row.id);
    });

    // Fetch releases for the artist (useful for release associations)
    const [releaseError, releaseResult] = await noderr(
      supabase
        .from('releases')
        .select('*, artist:artists(*)')
        .eq('artist_id', artistId)
    );

    if (releaseError) {
      console.error('Error fetching releases for artist:', releaseError);
      throw new DatabaseError(`Failed to fetch releases for artist: ${releaseError.message}`);
    }

    const releaseRows = (((releaseResult as any)?.data) ?? []) as any[];
    const releaseMap = new Map<string, any>();
    const releaseIds: string[] = [];

    releaseRows.forEach((release) => {
      if (release?.id) {
        releaseMap.set(release.id, release);
        releaseIds.push(release.id);
      }
    });

    // Associations directly to the artist
    const artistAssociationRows = await this.fetchAssociationRows('artist', [artistId]);
    artistAssociationRows.forEach((row) => {
      campaignIds.add(row.campaign_id);
    });

    // Associations via releases owned by the artist
    const releaseAssociationRows = await this.fetchAssociationRows('release', releaseIds);
    releaseAssociationRows.forEach((row) => {
      campaignIds.add(row.campaign_id);
    });

    if (!campaignIds.size) {
      console.debug('No campaigns associated with artist');
      return [];
    }

    const metaCampaigns = await this.fetchMetaCampaignsByIds(Array.from(campaignIds));

    const releaseOverrides = new Map<string, any>();
    releaseAssociationRows.forEach((row) => {
      const release = releaseMap.get(row.entity_id);
      if (release && !releaseOverrides.has(row.campaign_id)) {
        releaseOverrides.set(row.campaign_id, release);
      }
    });

    // Ensure campaigns directly linked by release inherit release data if available
    metaCampaigns.forEach((campaign) => {
      const releaseId = campaign.internal_campaign_id || campaign.release_id;
      if (releaseId && !releaseOverrides.has(campaign.id)) {
        const release = releaseMap.get(releaseId);
        if (release) {
          releaseOverrides.set(campaign.id, release);
        }
      }
    });

    const campaigns = metaCampaigns.map((campaign) =>
      this.mapMetaCampaignToCampaign(campaign, releaseOverrides.get(campaign.id))
    );

    campaigns.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

    console.debug('Fetched campaigns for artist (including associations):', campaigns.length);
    return campaigns;
  }

  // Get campaigns linked to a specific release (Meta campaigns only)
  async getCampaignsByReleaseId(releaseId: string): Promise<Campaign[]> {
    console.debug('CampaignService.getCampaignsByReleaseId called with releaseId:', releaseId);
    
    const releaseOverrides = new Map<string, any>();

    // Fetch release record for context (optional)
    let releaseRecord: any = null;
    const [releaseError, releaseResult] = await noderr(
      supabase
        .from('releases')
        .select('*, artist:artists(*)')
        .eq('id', releaseId)
        .maybeSingle()
    );

    if (releaseError) {
      console.warn('Unable to fetch release details for campaign lookup:', releaseError?.message);
    } else {
      releaseRecord = (releaseResult as any)?.data ?? null;
    }

    const campaignIds = new Set<string>();

    // Direct linkage via internal_campaign_id
    const [directError, directResult] = await noderr(
      supabase
        .from('meta_campaigns')
        .select('id')
        .eq('internal_campaign_id', releaseId)
    );

    if (directError) {
      console.error('Error fetching direct campaigns for release:', directError);
      throw new DatabaseError(`Failed to fetch campaigns for release: ${directError.message}`);
    }

    const directRows = (((directResult as any)?.data) ?? []) as Array<{ id: string }>;
    directRows.forEach((row) => {
      if (row?.id) {
        campaignIds.add(row.id);
      }
    });

    // Associations stored in campaign_associations
    const associationRows = await this.fetchAssociationRows('release', [releaseId]);
    associationRows.forEach((row) => {
      campaignIds.add(row.campaign_id);
      if (releaseRecord) {
        releaseOverrides.set(row.campaign_id, releaseRecord);
      }
    });

    if (!campaignIds.size) {
      console.debug('No campaigns associated with release');
      return [];
    }

    const metaCampaigns = await this.fetchMetaCampaignsByIds(Array.from(campaignIds));

    metaCampaigns.forEach((campaign) => {
      const linkedReleaseId = campaign.internal_campaign_id || campaign.release_id;
      if (releaseRecord && linkedReleaseId === releaseId && !releaseOverrides.has(campaign.id)) {
        releaseOverrides.set(campaign.id, releaseRecord);
      }
    });

    const campaigns = metaCampaigns.map((campaign) =>
      this.mapMetaCampaignToCampaign(campaign, releaseOverrides.get(campaign.id))
    );

    campaigns.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

    console.debug('Fetched campaigns for release (including associations):', campaigns.length);
    return campaigns;
  }

  // Create a new Meta campaign linked to a release
  async createCampaignForRelease(releaseId: string, campaignData: {
    name: string;
    budget: number;
    start_date: string;
    end_date: string;
    status?: 'draft' | 'active' | 'paused' | 'completed';
  }): Promise<Campaign> {
    console.debug('CampaignService.createCampaignForRelease called:', { releaseId, campaignData });

    // Generate a unique campaign_id for Meta campaigns
    const metaCampaignId = `internal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const accountId = 'internal_account'; // Default account for internally created campaigns

    try {
      // First, ensure the default meta_ad_account exists
      console.debug('Ensuring meta_ad_account exists for internal campaigns');
      const [accountError] = await noderr(
        supabase
          .from('meta_ad_accounts')
          .upsert({
            account_id: accountId,
            account_name: 'Internal Campaigns Account',
            currency: 'USD',
            timezone_name: 'UTC',
            is_active: true,
            last_sync_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }, { onConflict: 'account_id' })
      );

      if (accountError) {
        console.error('Error ensuring meta_ad_account exists:', accountError);
        throw new DatabaseError(`Failed to create ad account: ${accountError.message}`);
      }

      // Now create the Meta campaign
      const [error, result] = await noderr(
        supabase
          .from('meta_campaigns')
          .insert({
            campaign_id: metaCampaignId,
            account_id: accountId,
            internal_campaign_id: releaseId, // Link to release
            name: campaignData.name,
            status: campaignData.status?.toUpperCase() || 'DRAFT',
            objective: 'CONVERSIONS', // Default objective
            budget: campaignData.budget,
            start_date: campaignData.start_date,
            end_date: campaignData.end_date,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select('*, release:releases!internal_campaign_id(*, artist:artists(*))')
          .single()
      );

      if (error) {
        console.error('Error creating Meta campaign for release:', error);
        throw new DatabaseError(`Failed to create campaign: ${error.message}`);
      }

      const metaCampaign = (result as any)?.data;
      const campaign = this.mapMetaCampaignToCampaign(metaCampaign, metaCampaign.release);
      
      console.debug('Created Meta campaign for release:', campaign);
      this.notifyCampaignsChanged();
      return campaign;
    } catch (error) {
      console.error('Error in createCampaignForRelease:', error);
      if (error instanceof DatabaseError) {
        throw error;
      }
      throw new DatabaseError(`Failed to create campaign: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Link existing Meta campaign to a release
  async linkCampaignToRelease(campaignId: string, releaseId: string): Promise<Campaign> {
    console.debug('CampaignService.linkCampaignToRelease called:', { campaignId, releaseId });

    const [error, result] = await noderr(
      supabase
        .from('meta_campaigns')
        .update({ 
          internal_campaign_id: releaseId,
          updated_at: new Date().toISOString()
        })
        .eq('id', campaignId)
        .select('*, release:releases!internal_campaign_id(*, artist:artists(*))')
        .single()
    );

    if (error) {
      console.error('Error linking Meta campaign to release:', error);
      throw new DatabaseError(`Failed to link campaign to release: ${error.message}`);
    }

    const metaCampaign = (result as any)?.data;
    const campaign = this.mapMetaCampaignToCampaign(metaCampaign, metaCampaign.release);
    
    console.debug('Linked Meta campaign to release:', campaign);
    this.notifyCampaignsChanged();
    return campaign;
  }

  // Unlink Meta campaign from release
  async unlinkCampaignFromRelease(campaignId: string): Promise<Campaign> {
    console.debug('CampaignService.unlinkCampaignFromRelease called:', { campaignId });

    const [error, result] = await noderr(
      supabase
        .from('meta_campaigns')
        .update({ 
          internal_campaign_id: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', campaignId)
        .select('*')
        .single()
    );

    if (error) {
      console.error('Error unlinking Meta campaign from release:', error);
      throw new DatabaseError(`Failed to unlink campaign from release: ${error.message}`);
    }

    const metaCampaign = (result as any)?.data;
    const campaign = this.mapMetaCampaignToCampaign(metaCampaign);
    
    console.debug('Unlinked Meta campaign from release:', campaign);
    this.notifyCampaignsChanged();
    return campaign;
  }

  async getCampaignCountsByEntity(): Promise<{
    perArtist: Record<string, { total: number; active: number }>;
    perRelease: Record<string, { total: number; active: number }>;
  }> {
    const [error, result] = await noderr(
      supabase
        .from('unified_campaigns')
        .select('id, artist_id, release_id, normalized_status')
    );

    if (error) {
      console.error('Error fetching campaign counts:', error);
      throw new DatabaseError(`Failed to fetch campaign counts: ${error.message}`);
    }

    const rows = ((result as any)?.data || []) as Array<{
      id: string;
      artist_id: string | null;
      release_id: string | null;
      normalized_status: string | null;
    }>;

    type Context = {
      status: string;
      releases: Set<string>;
      artists: Set<string>;
    };

    const contexts = new Map<string, Context>();
    const releaseIds = new Set<string>();

    rows.forEach((row) => {
      const status = (row.normalized_status || '').toLowerCase();
      const context: Context = {
        status,
        releases: new Set<string>(),
        artists: new Set<string>(),
      };

      if (row.release_id) {
        context.releases.add(row.release_id);
        releaseIds.add(row.release_id);
      }
      if (row.artist_id) {
        context.artists.add(row.artist_id);
      }

      contexts.set(row.id, context);
    });

    const campaignIds = Array.from(contexts.keys());

    // Enrich with associations from campaign_associations
    const chunkSize = 100;
    for (let i = 0; i < campaignIds.length; i += chunkSize) {
      const chunk = campaignIds.slice(i, i + chunkSize);
      const [assocError, assocResult] = await noderr(
        supabase
          .from('campaign_associations')
          .select('campaign_id, entity_type, entity_id')
          .eq('campaign_type', 'meta')
          .in('campaign_id', chunk)
      );

      if (assocError) {
        console.error('Error fetching campaign associations for counts:', assocError);
        throw new DatabaseError(`Failed to fetch campaign counts: ${assocError.message}`);
      }

      const associations = (((assocResult as any)?.data) ?? []) as Array<{
        campaign_id: string;
        entity_type: string;
        entity_id: string;
      }>;

      associations.forEach((assoc) => {
        const context = contexts.get(assoc.campaign_id);
        if (!context) return;

        if (assoc.entity_type === 'release') {
          context.releases.add(assoc.entity_id);
          releaseIds.add(assoc.entity_id);
        } else if (assoc.entity_type === 'artist') {
          context.artists.add(assoc.entity_id);
        }
      });
    }

    // Map releases to their artists for roll-up
    const releaseArtistMap = new Map<string, string>();
    if (releaseIds.size) {
      const releaseIdArray = Array.from(releaseIds);
      for (let i = 0; i < releaseIdArray.length; i += chunkSize) {
        const chunk = releaseIdArray.slice(i, i + chunkSize);
        const [releaseError, releaseResult] = await noderr(
          supabase
            .from('releases')
            .select('id, artist_id')
            .in('id', chunk)
        );

        if (releaseError) {
          console.error('Error fetching releases for counts:', releaseError);
        } else {
          const releaseRows = (((releaseResult as any)?.data) ?? []) as Array<{ id: string; artist_id: string | null }>;
          releaseRows.forEach((release) => {
            if (release.id && release.artist_id) {
              releaseArtistMap.set(release.id, release.artist_id);
            }
          });
        }
      }
    }

    contexts.forEach((context) => {
      context.releases.forEach((releaseId) => {
        const artistId = releaseArtistMap.get(releaseId);
        if (artistId) {
          context.artists.add(artistId);
        }
      });
    });

    type CountStats = { total: number; active: number };
    const perArtist = new Map<string, CountStats>();
    const perRelease = new Map<string, CountStats>();

    const increment = (map: Map<string, CountStats>, key: string, isActive: boolean) => {
      const next = map.get(key) ?? { total: 0, active: 0 };
      next.total += 1;
      if (isActive) next.active += 1;
      map.set(key, next);
    };

    contexts.forEach((context) => {
      const isActive = context.status === 'active';
      context.releases.forEach((releaseId) => {
        increment(perRelease, releaseId, isActive);
      });
      context.artists.forEach((artistId) => {
        increment(perArtist, artistId, isActive);
      });
    });

    const mapToRecord = (map: Map<string, CountStats>) => {
      const entries: Array<[string, CountStats]> = [];
      map.forEach((value, key) => {
        entries.push([key, { total: value.total, active: value.active }]);
      });
      return Object.fromEntries(entries);
    };

    return {
      perArtist: mapToRecord(perArtist),
      perRelease: mapToRecord(perRelease),
    };
  }

  async getCampaigns(filter: CampaignsFilter & { search?: string } = {}): Promise<Campaign[]> {
    console.debug('CampaignService.getCampaigns called with filter:', filter);
    
    // Use the unified_campaigns view for simplified data access
    let query = supabase
      .from('unified_campaigns')
      .select('*')
      .order('updated_at', { ascending: false });

    // Apply filters
    if (filter.status) {
      query = query.eq('normalized_status', filter.status);
    }
    if (filter.search) {
      query = query.or(`name.ilike.%${filter.search}%,release_title.ilike.%${filter.search}%,artist_name.ilike.%${filter.search}%`);
    }

    console.debug('Executing unified campaigns query...');
    const [error, result] = await noderr(query);

    if (error) {
      console.error('Error fetching unified campaigns:', error);
      throw new DatabaseError(`Failed to fetch campaigns: ${error.message}`);
    }

    const campaigns = ((result as any)?.data || []).map((uc: any) => {
      return {
        id: uc.id,
        name: uc.name,
        status: uc.normalized_status,
        start_date: uc.start_date,
        end_date: uc.end_date,
        budget: uc.budget || 0,
        created_at: uc.created_at,
        updated_at: uc.updated_at,
        release_id: uc.release_id,
        client_id: uc.client_id,
        source: 'meta', // All campaigns are now Meta campaigns
        external_campaign_id: uc.external_campaign_id,
        account_id: uc.account_id,
        objective: uc.objective,
        campaign_type: uc.campaign_type,
        spend: 0,
        impressions: 0,
        clicks: 0,
        ctr: 0,
        conversions: 0,
        cpm: 0,
        cpc: 0,
        reach: 0,
        frequency: 0,
        // Additional context from joins
        release_title: uc.release_title,
        artist_id: uc.artist_id,
        artist_name: uc.artist_name,
        client_name: uc.client_name,
        // Meta-specific fields
        daily_budget: uc.daily_budget,
        lifetime_budget: uc.lifetime_budget,
        last_sync_at: uc.last_sync_at
      } as Campaign;
    });
    
    const metricsMap = await fetchMetaInsightMetrics(
      'campaign',
      campaigns.map((campaign) => (campaign.external_campaign_id || '').toString()),
      {}
    );

    const enriched = campaigns.map((campaign) => {
      const metrics = normalizeMetricsRow(
        metricsMap.get((campaign.external_campaign_id || '').toString())
      );
      return {
        ...campaign,
        spend: metrics.spend,
        impressions: metrics.impressions,
        clicks: metrics.clicks,
        ctr: metrics.ctr,
        conversions: metrics.conversions,
      };
    });

    console.debug('Fetched unified campaigns:', enriched.length, enriched);
    return enriched;
  }
}

export const campaignService = new CampaignService();
